import './Statistics.css'
import { Alert, Button, ButtonGroup, Col, InputGroup, InputGroupText, Nav, NavItem, NavLink, Progress, Row, TabContent, TabPane } from "reactstrap"
import { useGetStatisticsQuery } from "../../backoffice/apis/statisticsAPI";
import { useTranslation } from 'react-i18next';
import { PromotionCriteria } from '../../backoffice/dtos/promotions/promotionCriteria';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getUserReadableDateRange, rangeParse } from '../../helpers/Types';
import { faFileExcel, faFilter, faX } from '@fortawesome/free-solid-svg-icons';
import CriteriaModal from '../criteria/CriteriaModal';
import { useMemo, useState } from 'react';
import Statistics from './Statistics';
import { downloadStatisticsExcel } from '../../backoffice/apis/excelAPI';
import { StatisticsModel } from '../../backoffice/dtos/statistics/statisticsModel';
import { SelectOption } from '../../helpers/SelectOption';
import { StatisticsGroupModel } from '../../backoffice/dtos/statistics/statisticsGroupModel';
import Select from 'react-select';


export default function StatisticsBody(
    {
        initialFilter,
        setSelectedPromotionCriteria,
        promotionCriteria,
        toggle
    }: {
        initialFilter: PromotionCriteria,
        setSelectedPromotionCriteria: (criteria: PromotionCriteria) => void
        promotionCriteria: PromotionCriteria,
        toggle: () => void
    }
) {
    const { t } = useTranslation(['statistics'])
    const { data: statisticsModel, isFetching, isError } = useGetStatisticsQuery(promotionCriteria, { refetchOnMountOrArgChange: true })

    if (!statisticsModel) {
        if (isError)
            return <Alert color='danger' className='m-3'>{t('load_error')}</Alert>

        return <Progress animated striped value={100} className='m-3' />
    }

    return <InnerStatisticsBody
        initialFilter={initialFilter}
        setSelectedPromotionCriteria={setSelectedPromotionCriteria}
        promotionCriteria={promotionCriteria}
        toggle={toggle}
        statisticsModel={statisticsModel}
        isFetching={isFetching}
        isError={isError}
    />
}

function InnerStatisticsBody(
    {
        initialFilter,
        setSelectedPromotionCriteria,
        promotionCriteria,
        toggle,
        statisticsModel,
        isFetching,
        isError
    }: {
        initialFilter: PromotionCriteria,
        setSelectedPromotionCriteria: (criteria: PromotionCriteria) => void
        promotionCriteria: PromotionCriteria,
        toggle: () => void,
        statisticsModel: StatisticsModel,
        isFetching: boolean,
        isError: boolean
    }
) {
    const { t } = useTranslation(['statistics', 'promotion_timeline', 'common'])
    const [criteriaToggle, setCriteriaToggle] = useState<boolean>(false);
    const [selectedTab, setActiveTab] = useState('0');
    // Only the id is kept in state; the group itself is always looked up in the latest
    // statisticsModel so that refetched data (e.g. after a criteria change) is displayed.
    const [selectedGroupId, setSelectedGroupId] = useState<number | undefined>(statisticsModel.statisticsGroupModels[0]?.id)
    const selectedGroup: StatisticsGroupModel | undefined = statisticsModel.statisticsGroupModels.find(m => m.id === selectedGroupId)
        ?? statisticsModel.statisticsGroupModels[0]
    const options: {value: StatisticsGroupModel; label: string }[] = useMemo(() => {
        return statisticsModel.statisticsGroupModels.map(m => ({value: m, label: m.description}))
    }, [statisticsModel.statisticsGroupModels])
    const selection = options.find(m => m.value.id === selectedGroup?.id) ?? null
    const activeTab = Number(selectedTab) < (selectedGroup?.statisticsProductTypeModels.length ?? 0) ? selectedTab : '0'

    return (
        <>
            <Row className='m-3'>
                <Col className='d-flex flex-column justify-content-end'>
                    <strong>{t('statistics_title', { ns: 'promotion_timeline' })} {promotionCriteria.dateRange && getUserReadableDateRange(rangeParse(promotionCriteria.dateRange))}</strong>
                </Col>
                <Col sm="auto">
                    <ButtonGroup>
                        <Button onClick={() => setCriteriaToggle(rel => !rel)}><FontAwesomeIcon icon={faFilter} /></Button>
                        <Button color='success' onClick={async () => { await downloadStatisticsExcel(promotionCriteria) }}><FontAwesomeIcon icon={faFileExcel} /></Button>
                    </ButtonGroup>
                </Col>
                <Col sm={1} md={1} className='justify-content-end d-flex flex-row'>
                    <Button color='danger' onClick={toggle}><FontAwesomeIcon icon={faX} /></Button>
                </Col>
            </Row>
            <div className='mx-3' style={{ height: '0.5rem' }}>
                {isFetching && <Progress animated striped value={100} style={{ height: '0.5rem' }} />}
            </div>
            {isError && !isFetching && <Alert color='danger' className='m-3'>{t('load_error')}</Alert>}
            <Row className='mb-4 mt-5'>
                <Col sm="5">
                    <InputGroup>
                        <InputGroupText>{t('Department', { ns: 'common' })}</InputGroupText>
                    <Select
                        isClearable={false}
                        className='react-select w-75'
                        classNamePrefix='select'
                        options={options}
                        value={selection}
                        onChange={(choice) => {
                            if (!choice)
                                return
                            setSelectedGroupId(choice.value.id)
                            setActiveTab('0')
                        }}
                        onBlur={_ => {}}
                        />
                    </InputGroup>
                </Col>
            </Row>
            <Nav tabs>
                {
                    selectedGroup && selectedGroup.statisticsProductTypeModels.map(
                        (it, i) => {
                            return (
                                <NavItem key={i}>
                                    <NavLink active={activeTab == `${i}`} onClick={() => { setActiveTab(`${i}`) }}>
                                        {
                                            it.description
                                        }
                                    </NavLink>
                                </NavItem>
                            )
                        }
                    )
                }
            </Nav>
            <TabContent activeTab={activeTab} style={{ opacity: isFetching ? 0.5 : 1 }}>
                {
                    selectedGroup && selectedGroup.statisticsProductTypeModels.map(
                        (it, i) => {
                            return (
                                <TabPane key={i} tabId={`${i}`}>
                                    <Statistics statisticsModel={it} measurementType={selectedGroup.measurementType} />
                                </TabPane>
                            )
                        }
                    )
                }
            </TabContent>
            <CriteriaModal
                setSelectedPromotionCriteria={setSelectedPromotionCriteria}
                toggle={() => setCriteriaToggle(rel => !rel)}
                initialFilter={initialFilter}
                isOpen={criteriaToggle}
                currentFilter={promotionCriteria}
            />
        </>
    )
}